import { useEffect, useState } from 'react'
import { Switch, Route } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { ToastProvider } from 'react-toast-notifications'
import { ErrorBoundary } from 'react-error-boundary'
import semver from 'semver'
import Home from '../home/Home'
import FullscreenError from '../fullscreen-error/FullscreenError'
import ToastMessage from '../toast/ToastMessage'
import SearchPage from '../search/SearchPage.js'
import VirtualBackgroundStatus from '../vb/VirtualBackgroundStatus'
import SharedImagePage from '../sharedImage/SharedImagePage'
import { selectIsAuthorized } from '../search/reducer'
import {
  selectZaRunningContext,
  selectZaConfigError,
  selectIsZaConfigScheduled,
  selectZaConfig,
} from './reducer'
import {
  getCurrentBackground,
  setCurrentImage,
  setIsVbSettingImage,
} from '../vb/actions'
import {
  selectIsVbSettingImage,
  selectPreviousImage,
  selectVbImage,
} from '../vb/reducer'
import { VB_IMAGE_TYPES } from '../vb/constants.js'
import * as CONSTANTS from './constants'
import './App.css'
import { zaGetAppContext } from './actions.js'

function ErrorFallback({ error, resetErrorBoundary }) {
  return <FullscreenError message={CONSTANTS.ERROR_MESSAGE_GENERIC} />
}

export default function App() {
  const dispatch = useDispatch()
  const zaRunningContext = useSelector(selectZaRunningContext)
  const zaConfigError = useSelector(selectZaConfigError)
  const isZaConfigScheduled = useSelector(selectIsZaConfigScheduled)
  const isAuthorized = useSelector(selectIsAuthorized)
  const zaConfigOutput = useSelector(selectZaConfig)
  const supportedMeetingContexts = ['inWebinar', 'inMeeting']
  const allSupportedRunningContexts = ['inWebinar', 'inMeeting', 'inMainClient']
  const [isInMainClientSupported, setIsInMainClientSupported] = useState(null)
  const previousVbImage = useSelector(selectPreviousImage)
  const hasPreviousVbImage = Boolean(
    previousVbImage?.id || previousVbImage?.type
  )
  const isVbSettingImage = useSelector(selectIsVbSettingImage)
  const vbImage = useSelector(selectVbImage)

  useEffect(() => {
    if (zaConfigOutput) {
      dispatch(zaGetAppContext())
    }
  }, [dispatch, zaConfigOutput])

  /**
   * Upon the initial app load, we retrieve the currentBackgroundId in case the user sets the very first background and decides to revert to the original one.
   */
  useEffect(() => {
    const onMyMediaChange = ({ media }) => {
      const isVideoOn = media.video.state
      if (isVideoOn && !hasPreviousVbImage) {
        // We invoke getCurrentBackgroundId again when users turn on their video for the first time
        dispatch(getCurrentBackground())
      }
    }

    if (zaConfigOutput && !hasPreviousVbImage) {
      dispatch(getCurrentBackground())
      window.zoomSdk.addEventListener('onMyMediaChange', onMyMediaChange)
    }

    return () => {
      if (zaConfigOutput) {
        window.zoomSdk.removeEventListener('onMyMediaChange', onMyMediaChange)
      }
    }
  }, [zaConfigOutput, dispatch, hasPreviousVbImage])

  useEffect(() => {
    const onSetVirtualBackground = (event) => {
      if (
        event.backgroundSetting === VB_IMAGE_TYPES.blur &&
        vbImage?.type !== VB_IMAGE_TYPES.blur &&
        !isVbSettingImage
      ) {
        dispatch(setCurrentImage({ type: VB_IMAGE_TYPES.blur }))
      } else if (
        event.backgroundSetting === 'background' &&
        !isVbSettingImage
      ) {
        /**
         * setVirtualBackground can be invoked by either VB app or Client setting.
         * We only want to override the current image state(redux) when setVirtualBackground is invoked by Client.
         * To know which party is invoking setVirtualBackground, we can simply check isVbSettingImage, which is redux state of VB App
         */
        dispatch(setCurrentImage({ id: event.background.id }))
      }

      /**
       * We must set setIsVbSettingImage back to false HERE; Otherwise, setting background via Client will not update background history stack
       */
      dispatch(setIsVbSettingImage(false))
    }

    if (zaConfigOutput) {
      window.zoomSdk.addEventListener(
        'onSetVirtualBackground',
        onSetVirtualBackground
      )
    }

    return () => {
      if (zaConfigOutput) {
        window.zoomSdk.removeEventListener(
          'onSetVirtualBackground',
          onSetVirtualBackground
        )
      }
    }
  }, [zaConfigOutput, dispatch, isVbSettingImage, vbImage])

  useEffect(() => {
    const onRemoveVirtualBackground = () => {
      if (!isVbSettingImage && vbImage?.type !== VB_IMAGE_TYPES.none) {
        dispatch(setCurrentImage({ type: VB_IMAGE_TYPES.none }))
      }

      dispatch(setIsVbSettingImage(false))
    }

    if (zaConfigOutput) {
      window.zoomSdk.addEventListener(
        'onRemoveVirtualBackground',
        onRemoveVirtualBackground
      )
    }

    return () => {
      if (zaConfigOutput) {
        window.zoomSdk.removeEventListener(
          'onRemoveVirtualBackground',
          onRemoveVirtualBackground
        )
      }
    }
  }, [zaConfigOutput, dispatch, isVbSettingImage, vbImage])

  useEffect(() => {
    if (zaConfigOutput) {
      // only need first three numbers of the version 5.13.5.XXXX
      const currentClientVersion = zaConfigOutput.clientVersion
        .split('.', 3)
        .join('.')
      if (zaConfigOutput.product === 'mobile') {
        // userAgent string for iPhone and iPad is looks like: Mozilla/5.0(iPhone; CPU iPhone OS 16_0_3 like Mac OS X) AppleWebKit/605.1.15(KHTML, like Gecko) Mobile/15E148
        const isProductiOS = zaConfigOutput.userAgent.includes('iPhone')
        // returns true if currentClientVersion >= 5.13.10
        const isiOSClientSupported = semver.gte(
          currentClientVersion,
          CONSTANTS.INMAINCLIENT_SUPPORT_FROM_IOS_CLIENT_VERSION
        )
        setIsInMainClientSupported(isiOSClientSupported && isProductiOS)
      } else if (zaConfigOutput.product === 'desktop') {
        // returns true if currentClientVersion >= 5.13.5
        const isDesktopClientSupported = semver.gte(
          currentClientVersion,
          CONSTANTS.INMAINCLIENT_SUPPORT_FROM_DESKTOP_CLIENT_VERSION
        )
        setIsInMainClientSupported(isDesktopClientSupported)
      }
    }
  }, [zaConfigOutput, isInMainClientSupported])

  // An application breaking error occurred
  if (zaConfigError || !isAuthorized) {
    return <FullscreenError message={CONSTANTS.ERROR_MESSAGE_GENERIC} />
  } else if (isZaConfigScheduled) {
    if (
      !isInMainClientSupported &&
      !supportedMeetingContexts.includes(zaRunningContext)
    ) {
      return (
        <FullscreenError message={CONSTANTS.ERROR_MESSAGE_VB_IN_MEETINGS} />
      )
    } else if (!allSupportedRunningContexts.includes(zaRunningContext)) {
      return <FullscreenError message={CONSTANTS.ERROR_MESSAGE_GENERIC} />
    }
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <div className="App">
        <Switch>
          <Route exact path="/">
            <Home />
          </Route>
          <Route path="/search">
            <SearchPage />
          </Route>
          <Route path="/shared/image">
            <SharedImagePage />
          </Route>
        </Switch>
        <ToastProvider
          placement="bottom-center"
          components={{ Toast: ToastMessage }}
        >
          <VirtualBackgroundStatus />
        </ToastProvider>
      </div>
    </ErrorBoundary>
  )
}
